import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Loader2, Download, ArrowLeft } from "lucide-react";
import { toPng } from "html-to-image";

export default function SuccessPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const capsuleId = params.get("capsule_id");
  const isPromo = params.get("promo") === "1";

  const [paymentStatus, setPaymentStatus] = useState(
    sessionId ? "polling" : (isPromo && capsuleId ? "polling" : "unknown")
  );
  const [capsule, setCapsule] = useState(null);
  // The "preview email" modal was intentionally removed — its endpoint
  // used to render the fully-composed delivery email (letter body + AI
  // reply) the moment payment cleared, which broke the sealed-until-
  // midnight-of-the-appointed-date promise. The endpoint itself now
  // returns 403 until `delivery_date`; the client no longer even calls
  // it here. See privacy audit: 0% content visibility pre-delivery.
  const cardRef = useRef();
  const attemptsRef = useRef(0);
  const cancelledRef = useRef(false);
  const fallbackYear = useMemo(() => new Date().getFullYear() + 1, []);

  // Promo path: no Stripe session — just fetch the (already-paid) capsule.
  useEffect(() => {
    if (!isPromo || !capsuleId) return;
    cancelledRef.current = false;
    (async () => {
      try {
        const c = await api.get(`/capsules/${capsuleId}`);
        if (cancelledRef.current) return;
        setCapsule(c.data);
        setPaymentStatus("paid");
      } catch {
        setPaymentStatus("error");
      }
    })();
    return () => { cancelledRef.current = true; };
  }, [isPromo, capsuleId]);

  // ─── Session-scoped "sealed" marker ─────────────────────────────────
  // Once the user lands on /success with a confirmed capsule (paid or
  // promo-redeemed), stash a compact record in sessionStorage. The
  // landing-page envelope demo reads this flag and, if present, hides
  // the sample-letter demo (which would otherwise be confusing — the
  // user is no longer prospecting, they've already sealed their real
  // letter). Cleared automatically when the browser tab closes.
  //
  // Also — CRITICALLY — clear `mp_letter_draft` from localStorage here.
  // The draft only exists to protect an in-flight composition from
  // being lost when the user cancels Stripe or reloads. Once the
  // capsule is confirmed paid, the draft has fulfilled its purpose
  // and must be evicted so the user starts a fresh compose next time.
  useEffect(() => {
    if (!capsule || paymentStatus !== "paid") return;
    try {
      sessionStorage.setItem(
        "mp-sealed",
        JSON.stringify({
          capsule_id: capsule.id,
          duration_days: capsule.duration_days,
          delivery_date: capsule.delivery_date,
          sealed_at: new Date().toISOString(),
        })
      );
      localStorage.removeItem("mp_letter_draft");
    } catch { /* private-mode / quota / SSR — silently ignore */ }
  }, [capsule, paymentStatus]);

  useEffect(() => {
    if (!sessionId) return;
    cancelledRef.current = false;
    attemptsRef.current = 0;
    // Progressive backoff — first 3 attempts fast (card + wallets usually settle
    // in <5 s), next 5 medium (~2-6 s), last 7 slower (WeChat Pay / Alipay QR
    // flows sometimes need 30-90 s for the async webhook to fire).
    const delayFor = (n) => (n <= 3 ? 2000 : n <= 8 ? 3000 : 5000);
    const MAX_ATTEMPTS = 15;

    const poll = async () => {
      attemptsRef.current += 1;
      // After 3 fast attempts without success, escalate copy to the async
      // "whispering to your bank" message so the UI feels intentional rather
      // than broken during Alipay/WeChat settlement.
      if (attemptsRef.current === 4) setPaymentStatus("polling_async");
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (cancelledRef.current) return;
        if (res.data.payment_status === "paid") {
          if (capsuleId) {
            const c = await api.get(`/capsules/${capsuleId}`);
            if (cancelledRef.current) return;
            setCapsule(c.data);
          }
          setPaymentStatus("paid");
          return;
        }
        if (res.data.status === "expired") {
          setPaymentStatus("expired");
          return;
        }
        if (attemptsRef.current < MAX_ATTEMPTS) {
          setTimeout(poll, delayFor(attemptsRef.current));
        } else {
          setPaymentStatus("timeout");
        }
      } catch {
        if (attemptsRef.current < MAX_ATTEMPTS) {
          setTimeout(poll, delayFor(attemptsRef.current));
        } else {
          setPaymentStatus("error");
        }
      }
    };
    poll();
    return () => {
      cancelledRef.current = true;
    };
  }, [sessionId, capsuleId]);

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      // Pre-collect same-origin font CSS to inline into the PNG.
      let fontEmbedCSS = "";
      try {
        const sheets = Array.from(document.styleSheets);
        const parts = [];
        for (const s of sheets) {
          try {
            for (const rule of s.cssRules) {
              if (rule.cssText && rule.cssText.startsWith("@font-face")) {
                parts.push(rule.cssText);
              }
            }
          } catch { /* cross-origin — skip */ }
        }
        fontEmbedCSS = parts.join("\n");
      } catch {
        fontEmbedCSS = "";
      }

      // Instead of cloning (which strips computed styles and breaks the
      // certificate rendering), we use html-to-image's built-in `style`
      // option to inject explicit CSS variables + a padded parchment frame
      // AROUND the certificate at serialize time. This guarantees the
      // export has:
      //   • an explicit light parchment background (#FBF5E7)
      //   • high-contrast text colors resolved from CSS custom properties
      //   • generous padding so the card sits like a printed keepsake
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#FBF5E7",
        fontEmbedCSS,
        style: {
          // Inline CSS-var declarations for the exported node so wax-red,
          // ink, and rule colors resolve cleanly during serialization.
          "--ink": "#1C1A17",
          "--ink-soft": "#4A4137",
          "--ink-mute": "#8B8275",
          "--wax": "#8A3A33",
          "--wax-deep": "#6E2D28",
          "--bg-paper": "#FBF5E7",
          "--rule": "#C5B698",
          // Breathing room around the certificate so the parchment page
          // color frames it like a physical print.
          padding: "40px",
          background: "#FBF5E7",
        },
      });

      const link = document.createElement("a");
      link.download = `miprose-${(capsule?.id || "card").slice(0, 8)}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error(e);
    }
  };

  if (paymentStatus === "polling" || paymentStatus === "polling_async") {
    const isAsync = paymentStatus === "polling_async";
    const msg = t(isAsync ? "checkout.polling_async" : "checkout.polling");
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center" data-testid="polling-container">
          {/* Elegant minimalist loader — a slowly-breathing wax dot rather
             than a bland spinner, matching the wabi-sabi tone. */}
          <div className="flex justify-center mb-8">
            <div className="relative w-3 h-3">
              <span className="absolute inset-0 rounded-full bg-[var(--wax)] animate-ping opacity-60"></span>
              <span className="absolute inset-0 rounded-full bg-[var(--wax)]"></span>
            </div>
          </div>
          <p
            data-testid="polling-msg"
            className="font-serif italic text-[17px] leading-[1.85] text-[var(--ink-soft)] text-balance"
          >
            {msg}
          </p>
        </div>
      </div>
    );
  }

  if (paymentStatus !== "paid") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
        <p className="text-[var(--ink-soft)] italic">{t("checkout.cancelled")}</p>
        <button onClick={() => navigate("/")} className="btn-wax px-6 py-3 text-[12px] uppercase tracking-[0.22em]">
          {t("checkout.write_another")}
        </button>
      </div>
    );
  }

  const deliveryDate = capsule
    ? new Date(capsule.delivery_date).toLocaleDateString(i18n.language, { year: "numeric", month: "long", day: "numeric" })
    : "";

  // Tier-aware copy: pick the 90/180/365 variant based on the paid capsule,
  // fall back to the legacy 365 key if the tier-suffixed key is missing.
  const tier = [90, 180, 365].includes(capsule?.duration_days) ? capsule.duration_days : 365;
  const subtitleKey = `checkout.success_subtitle_${tier}`;
  const shareTitleKey = `checkout.share_title_${tier}`;

  return (
    <div className="min-h-screen px-6 py-20 md:py-28">
      <div className="max-w-3xl mx-auto text-center">
        <div className="flex justify-center mb-10">
          <span className="wax-stamp">SEALED</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight" data-testid="success-title">
          {t("checkout.success_title")}
        </h1>
        <p className="mt-6 text-[19px] text-[var(--ink-soft)] leading-[1.9] max-w-2xl mx-auto">
          {t(subtitleKey, { defaultValue: t("checkout.success_subtitle") })}
        </p>
        <p className="mt-8 italic text-[var(--ink-soft)]">
          {t("checkout.delivery_label")}:{" "}
          <span className="not-italic text-[var(--ink)]" data-testid="success-delivery-date">{deliveryDate}</span>
        </p>

        <p className="mt-6 text-[12px] italic text-[var(--ink-mute)]">
          {t("checkout.rescue_hint")}{" "}
          <a
            href="/rescue"
            data-testid="success-rescue-link"
            className="not-italic uppercase tracking-[0.2em] text-[var(--wax)] hover:text-[var(--ink)] border-b border-[var(--wax)] hover:border-[var(--ink)] pb-0.5 ml-1"
          >
            {t("checkout.rescue_cta")}
          </a>
        </p>

        <div className="mt-20">
          <p className="text-[11px] uppercase tracking-[0.36em] text-[var(--wax)] mb-8">
            {t(shareTitleKey, { defaultValue: t("checkout.share_title") })}
          </p>
          <ShareCard cardRef={cardRef} capsule={capsule} fallbackYear={fallbackYear} />
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={downloadCard}
            data-testid="download-card-btn"
            className="btn-wax px-6 py-3 text-[12px] uppercase tracking-[0.22em] inline-flex items-center gap-2"
          >
            <Download size={14} /> {t("checkout.download_card")}
          </button>
          <button
            onClick={() => navigate("/")}
            data-testid="write-another-btn"
            className="px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)] inline-flex items-center gap-2"
          >
            <ArrowLeft size={14} /> {t("checkout.write_another")}
          </button>
        </div>
      </div>
    </div>
  );
}

const ShareCard = ({ cardRef, capsule, fallbackYear }) => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith("zh");
  const year = capsule?.delivery_date
    ? new Date(capsule.delivery_date).getFullYear()
    : fallbackYear;

  const targetMs = capsule?.delivery_date ? new Date(capsule.delivery_date).getTime() : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  let countdown = null;
  if (targetMs) {
    let diff = Math.max(0, targetMs - now);
    const days = Math.floor(diff / 86_400_000); diff -= days * 86_400_000;
    const hours = Math.floor(diff / 3_600_000); diff -= hours * 3_600_000;
    const minutes = Math.floor(diff / 60_000); diff -= minutes * 60_000;
    const seconds = Math.floor(diff / 1000);
    countdown = { days, hours, minutes, seconds };
  }

  const capsuleShortId = capsule?.id ? capsule.id.slice(0, 8).toUpperCase() : "—";
  const deliveryDateStr = capsule?.delivery_date
    ? new Date(capsule.delivery_date).toLocaleDateString(i18n.language, {
        year: "numeric", month: "long", day: "numeric",
      })
    : "";

  return (
    <div className="flex justify-center">
      <div ref={cardRef} className="cert" data-testid="share-card">
        <div className="cert-stamp">
          <div className="cert-stamp-label">SEALED</div>
          <div className="cert-stamp-year">{year}</div>
        </div>

        <div className="cert-ornament" data-testid="cert-ornament">
          {t("checkout.cert_ornament")}
        </div>

        <h3 className={`${isZh ? "font-cn" : "font-display"} text-center text-[28px] md:text-[32px] leading-tight tracking-tight text-[var(--ink)] mt-2`}>
          {t("checkout.success_title")}
        </h3>

        <p className="cert-divider">✦</p>

        <p
          className={`text-center ${isZh ? "font-cn" : "font-display"} italic text-[15px] text-[var(--ink-soft)] mb-3`}
        >
          {t("checkout.cert_sealed_for")}
        </p>
        <p className={`text-center ${isZh ? "font-cn" : "font-display"} text-[22px] text-[var(--ink)] mb-6`}>
          {capsule?.user_name || "—"}
        </p>

        {/* Countdown */}
        {countdown && (
          <div className="mt-2 mb-6" data-testid="cert-countdown">
            <p className="text-center text-[10px] uppercase tracking-[0.3em] text-[var(--wax)] mb-3">
              {t("checkout.countdown_label")}
            </p>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[
                ["days", countdown.days],
                ["hours", countdown.hours],
                ["minutes", countdown.minutes],
                ["seconds", countdown.seconds],
              ].map(([k, v]) => (
                <div key={k} className="bg-[var(--bg-paper)] border border-[var(--rule)] py-3">
                  <div className="font-display text-[26px] text-[var(--ink)] countdown-tick" data-testid={`countdown-${k}`}>
                    {String(v).padStart(2, "0")}
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-[var(--ink-soft)] mt-0.5">
                    {t(`checkout.countdown_${k}`)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-end justify-between border-t border-dashed border-[var(--rule)] pt-4 mt-2 text-[12px]">
          <div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
              {t("checkout.capsule_id_label")}
            </div>
            <div className="font-display tracking-[0.16em] text-[var(--ink)] mt-1" data-testid="cert-capsule-id">
              № {capsuleShortId}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
              {t("checkout.delivery_label")}
            </div>
            <div className={`${isZh ? "font-cn" : "font-display"} text-[var(--ink)] mt-1`}>
              {deliveryDateStr}
            </div>
          </div>
        </div>

        <p className="text-center text-[10px] italic text-[var(--ink-mute)] mt-6 tracking-[0.05em]">
          {t("checkout.cert_signed")}
        </p>
      </div>
    </div>
  );
};
